import { useEffect, useRef, useState } from 'react'
import { useVoiceStore } from '../../stores/voiceStore'
import { useBirthdayStore } from '../birthday/birthdayStore'

/** Birthday-userIds present in the channel that haven't been celebrated yet this session. */
export function selectNewlyCelebrating(presentBirthdayIds: string[], already: Set<string>): string[] {
  return presentBirthdayIds.filter(id => !already.has(id))
}

export interface Celebration { key: number; names: string[] }

/** Usernames sometimes carry a "[tag]" — strip it for the banner. */
function cleanName(username: string): string {
  return username.replace(/\s*\[.*?\]\s*/g, '').trim() || username
}

/**
 * Watches the current voice channel's participants + today's birthday set and
 * emits a Celebration whenever a birthday person becomes newly present (their
 * join, or the local user joining a channel that already has one). Dedups per
 * channel session; resets when the channel changes.
 */
export function useVoiceBirthdayTrigger(): Celebration | null {
  const participants = useVoiceStore(s => s.participants)
  const currentChannelId = useVoiceStore(s => s.currentChannelId)
  const todayIds = useBirthdayStore(s => s.todayIds)
  const celebrated = useRef<Set<string>>(new Set())
  const keyRef = useRef(0)
  const [celebration, setCelebration] = useState<Celebration | null>(null)

  // Reset dedup when switching/leaving channels so re-joining re-celebrates.
  useEffect(() => { celebrated.current = new Set() }, [currentChannelId])

  useEffect(() => {
    const present = participants.filter(p => todayIds.has(p.userId))
    const fresh = selectNewlyCelebrating(present.map(p => p.userId), celebrated.current)
    if (fresh.length === 0) return
    fresh.forEach(id => celebrated.current.add(id))
    const names = present.filter(p => fresh.includes(p.userId)).map(p => cleanName(p.user.username))
    keyRef.current += 1
    setCelebration({ key: keyRef.current, names })
  }, [participants, todayIds, currentChannelId])

  return celebration
}
